
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import { RunnitNodeRunResult, RunnitUpload } from '@run-diffusion/shared';
import { uploadImageRecordForUseCase } from '@/utils/runnitUpload';
import { fetchRunnitImageSrc } from '@/utils/fetchRunnitImageSrc';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { IMAGE_GALLERY_DIALOG_NAV } from '@/components/ImageGallery/utils';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import BaseButton from '@/components/base/BaseButton.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import ComplexBackground from '@/components/designElements/ComplexBackground.vue';
import ColorInput from '@/components/ColorInput.vue';
import ImageUpload from '@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue';
import Use3DTooltipCard from '@/views/Runnits/RunnitSettings/Pose3DEditor/Use3DTooltipCard.vue';
import {
	bgModeFromIndex,
	bgModeToIndex,
	buildCanvasBackgroundStyle,
	POSE_3D_BG_MODE,
	Pose3DBgMode,
	resolveBgModeAfterImageFailure,
	resolveCaptureBackgroundImageUrl,
	usesTransparentScene,
} from '@/views/Runnits/RunnitSettings/Pose3DEditor/pose3DBackground';
import {
	ASSET_3D_SUPPORTED_EXTENSIONS,
	getAsset3DExtension,
} from '@/views/Runnits/RunnitSettings/Asset3D/asset3DFormats';
import {
	Asset3DViewerHandle,
	attachAsset3DFramingPan,
	createAsset3DViewer,
	frameAsset3DCamera,
	renderAsset3DViewerToPngBlob,
	setAsset3DViewerBackground,
} from '@/views/Runnits/RunnitSettings/Asset3D/babylonAsset3D';

const CAPTURE_SIZE = 1024;

// Pose editing needs faster/tighter camera controls than the result viewer.
// Babylon's panning/rotation sensibilities are inverse (lower = faster); the
// shared viewer defaults (1200 / 12000) feel sluggish for posing.
const POSE_3D_VIEWER_ROTATION_SENSIBILITY = 1000;
const POSE_3D_VIEWER_WHEEL_DELTA_PERCENTAGE = 0.01;


export default Vue.extend({
	name: 'Pose3DEditor',
	mixins: [
		RunnitsImageSelectMixin,
	],
	props: {
		fieldUuid: { type: String, required: true },
		// Only needed to open the shared image library, which keys its selection
		// state off the field awaiting a selection.
		field: { type: Object, default: null },
	},
	data () {
		return {
			POSE_3D_BG_MODE,
			loading: false,
			loadingMessage: 'Loading 3D engine...',
			loadError: null as string | null,
			bgMode: POSE_3D_BG_MODE.TRANSPARENT as Pose3DBgMode,
			bgColor: '#808080',
			bgImageUrl: null as string | null,
			bgImageLoading: false,
			// Babylon objects are intentionally not reactive — keep them on `this` outside Vue's reactivity.
			viewer: null as Asset3DViewerHandle | null,
			detachFramingPan: null as (null | (() => void)),
		};
	},
	computed: {
		/**
		 * Camera bindings are Babylon's ArcRotateCamera defaults. Touch has no
		 * right-click or modifier key, so pointer devices and touch get different
		 * wording rather than one instruction that is wrong for half of users.
		 */
		controlHints (): { icon: string, label: string, gesture: string }[] {
			const isTouch: boolean = !!this.$vuetify.breakpoint.smAndDown;
			return [
				{
					icon: 'mdi-rotate-3d-variant',
					label: 'Rotate',
					gesture: isTouch ? 'drag with one finger' : 'drag',
				},
				{
					icon: 'mdi-cursor-move',
					label: 'Move',
					gesture: isTouch ? 'drag with two fingers' : 'right-drag, or hold Ctrl and drag',
				},
				{
					icon: 'mdi-magnify',
					label: 'Zoom',
					gesture: isTouch ? 'pinch' : 'scroll',
				},
			];
		},
		...mapState([
			'runnitState',
		]),
		fieldEditorState (): { open: boolean, saving: boolean, assetFile: File | null, assetName: string | null } {
			return this.runnitState.pose3DEditorStates[this.fieldUuid];
		},
		bgModeIndex (): number {
			return bgModeToIndex(this.bgMode);
		},
		isTransparentBgMode (): boolean {
			return this.bgMode === POSE_3D_BG_MODE.TRANSPARENT;
		},
		isColorBgMode (): boolean {
			return this.bgMode === POSE_3D_BG_MODE.COLOR;
		},
		isImageBgMode (): boolean {
			return this.bgMode === POSE_3D_BG_MODE.IMAGE;
		},
		useTransparentBg (): boolean {
			return usesTransparentScene(this.bgMode);
		},
		captureBackgroundImageUrl (): string | null {
			return resolveCaptureBackgroundImageUrl(this.bgMode, this.bgImageUrl);
		},
		canvasBackgroundStyle (): Record<string, string> {
			return buildCanvasBackgroundStyle(this.bgMode, this.bgImageUrl);
		},
		bgImageSelectionTriggerId (): string {
			return `pose3d-bg-${this.fieldUuid}`;
		},
		bgImageSelectionState (): any {
			return this.getSelectionStateByTrigger(this.bgImageSelectionTriggerId);
		},
	},
	watch: {
		bgMode () {
			this.applyBackground();
		},
		bgColor () {
			if (this.isColorBgMode) this.applyBackground();
		},
		bgImageSelectionState: {
			immediate: false,
			handler (newVal, oldVal) {
				const asset = this.getPromptSelectionStateAsset(newVal, oldVal);
				if (asset) this.applyBgImageSelection(asset);
			},
		},
	},
	mounted () {
		this.$nextTick(() => this.setupViewer());
	},
	beforeDestroy () {
		// Only if this editor ever opened the library. See clearBgImage.
		if (this.bgImageSelectionState) {
			this.exitSelectionMode(this.bgImageSelectionTriggerId);
		}
		this.teardownViewer();
	},
	methods: {
		...mapActions([
			'updatePose3DEditorState',
			'removePose3DEditorState',
			'updateRunnitState',
			'updateSnackbar',
		]),
		onDialogOpenStateChange (open: boolean) {
			if (!open) this.onClose();
		},
		onClose () {
			if (this.fieldEditorState.saving) return;
			this.removePose3DEditorState(this.fieldUuid);
		},
		onBgModeChange (idx: number) {
			this.bgMode = bgModeFromIndex(idx);
		},
		openBgImageLibrary () {
			if (!this.field) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Image backgrounds are not configured for this field',
					show: true,
				});
				return;
			}
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: IMAGE_GALLERY_DIALOG_NAV.GENERATIONS,
			});
			this.enterSelectionMode(
				NODE_RUN_RESULT_SELECTION_MODE.SINGLE,
				this.bgImageSelectionTriggerId,
				this.field,
				['IMG'],
			);
		},
		async applyBgImageSelection (asset: { nodeRunResult?: RunnitNodeRunResult, upload?: RunnitUpload }) {
			this.bgImageLoading = true;
			try {
				const { imageUrl } = asset.upload
					? await fetchRunnitImageSrc(asset.upload, 'upload')
					: await fetchRunnitImageSrc(asset.nodeRunResult, 'nodeRunResult');
				// Preloading here is what keeps a background that the browser cannot
				// fetch from silently becoming a capture failure after the user has
				// already posed the model against it.
				await this.preloadBgImage(imageUrl);
				this.bgImageUrl = imageUrl;
				this.bgMode = POSE_3D_BG_MODE.IMAGE;
			} catch (err: any) {
				console.error('Pose3DEditor background image selection failed', err);
				// Falls back to transparent when there is no earlier image to keep
				// showing, so a first-pick failure cannot leave the editor in image
				// mode with nothing behind the model.
				this.bgMode = resolveBgModeAfterImageFailure(this.bgImageUrl);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Could not load that background image. Please try another one.',
					show: true,
				});
			} finally {
				this.bgImageLoading = false;
			}
		},
		preloadBgImage (imageUrl: string): Promise<void> {
			return new Promise((resolve, reject) => {
				const img = new Image();
				// Matches the compositing path, so an image that cannot be captured
				// fails here instead of at capture time.
				img.crossOrigin = 'anonymous';
				img.onload = () => resolve();
				img.onerror = () => reject(new Error('Failed to load the background image'));
				img.src = imageUrl;
			});
		},
		onBgImageUploaded (upload: RunnitUpload) {
			if (!upload) return;
			this.applyBgImageSelection({ upload });
		},
		clearBgImage () {
			this.bgImageUrl = null;
			// Guarded because the shared helpers rebuild the entry from
			// emptySelectionState, whose triggeringId is null: calling them for a
			// trigger that never opened the library (the upload-only path) would
			// write the rebuilt state under the key "null" instead.
			if (this.bgImageSelectionState) {
				this.clearSelectionsFromState(this.bgImageSelectionTriggerId);
			}
		},
		async setupViewer () {
			const file = this.fieldEditorState.assetFile;
			if (!file) {
				this.loadError = 'No 3D file provided';
				return;
			}
			const ext = getAsset3DExtension(file.name);
			if (!ASSET_3D_SUPPORTED_EXTENSIONS.includes(ext)) {
				this.loadError = `Unsupported 3D file format: .${ext}`;
				return;
			}

			this.loading = true;
			this.loadError = null;
			this.loadingMessage = 'Loading 3D engine...';

			try {
				const canvas = this.$refs.renderCanvas as HTMLCanvasElement;
				if (!canvas) throw new Error('Render canvas not mounted');

				this.viewer = await createAsset3DViewer({
					canvas,
					file,
					extension: ext,
					transparentBackground: this.useTransparentBg,
					backgroundColor: this.bgColor,
					cameraName: 'pose3d-camera',
					rotationSensibility: POSE_3D_VIEWER_ROTATION_SENSIBILITY,
					wheelDeltaPercentage: POSE_3D_VIEWER_WHEEL_DELTA_PERCENTAGE,
					onBeforeLoadAsset: () => {
						this.loadingMessage = 'Loading asset...';
					},
				});

				// Panning is handled here rather than by Babylon so orbit keeps
				// pivoting on the model after a pan.
				this.detachFramingPan = attachAsset3DFramingPan(this.viewer, canvas);

				// Transform target and drag handling are built once the model is in
				// the scene, because both depend on its real bounds.
			} catch (err: any) {
				console.error('Pose3DEditor setup failed', err);
				this.loadError = err && err.message ? err.message : 'Failed to load 3D file';
			} finally {
				this.loading = false;
			}
		},
		resetView () {
			if (this.viewer) frameAsset3DCamera(this.viewer);
		},
		applyBackground () {
			if (!this.viewer) return;
			setAsset3DViewerBackground(this.viewer, this.useTransparentBg, this.bgColor);
		},
		async handleCapture () {
			// Capturing mid-preload would composite the background being replaced,
			// which the preview would then contradict a moment later.
			if (!this.viewer || this.bgImageLoading) return;
			await this.updatePose3DEditorState({
				fieldUuid: this.fieldUuid,
				editorState: { saving: true },
			});
			try {
				const blob = await this.renderToBlob();
				const upload = await this.uploadCapture(blob);
				this.$emit('input', { upload });
				this.removePose3DEditorState(this.fieldUuid);
			} catch (err: any) {
				console.error('Pose3DEditor capture failed', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: err && err.message ? err.message : 'Failed to capture 3D pose',
					show: true,
				});
				await this.updatePose3DEditorState({
					fieldUuid: this.fieldUuid,
					editorState: { saving: false },
				});
			}
		},
		async renderToBlob (): Promise<Blob> {
			if (!this.viewer) throw new Error('3D viewer is not ready');
			return renderAsset3DViewerToPngBlob(this.viewer, {
				width: CAPTURE_SIZE,
				height: CAPTURE_SIZE,
				transparentBackground: this.useTransparentBg,
				backgroundColor: this.bgColor,
				backgroundImageUrl: this.captureBackgroundImageUrl,
			});
		},
		async uploadCapture (blob: Blob): Promise<RunnitUpload> {
			const file = new File([blob], `${uuidv4()}.png`, { type: 'image/png' });
			return uploadImageRecordForUseCase<RunnitUpload>({
				file,
				uploadType: 'RUNNIT_UPLOAD',
			});
		},
		teardownViewer () {
			if (this.detachFramingPan) {
				this.detachFramingPan();
				this.detachFramingPan = null;
			}
			if (this.viewer) this.viewer.dispose();
			this.viewer = null;
			this.loadError = null;
			this.loading = false;
		},
	},
	components: {
		BaseButton,
		DialogOrBottomSheet,
		ComplexBackground,
		ColorInput,
		ImageUpload,
		Use3DTooltipCard,
	},
});
